import React, { useState, useRef, useCallback } from 'react';
import { Upload, FileText, X, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../lib/supabase';
import ResumeAnalysisService from '../../../services/resumeAnalysisService';
import { storeLiveAnalysisResults } from '../../../services/liveAnalysisStorage';
import { useNavigate } from 'react-router-dom';

const FileUploadZone = ({
  jobInput,
  onJobInputError,
  onAnalysisComplete,
  credits,
  hasCredits,
  userTier = 'free',
  previousAnalysisId = null
}) => {
  const { user, refreshUserProfile } = useAuth();
  const [dragActive, setDragActive] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisProgress, setAnalysisProgress] = useState(0);
  const [analysisStage, setAnalysisStage] = useState('');
  const [error, setError] = useState(null);
  const [usageLimitMessage, setUsageLimitMessage] = useState(null);
  const [analysisResults, setAnalysisResults] = useState(null);
  const [lastAnalysisTime, setLastAnalysisTime] = useState(null); // Rate limiting
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  const handleDrag = useCallback((event) => {
    event?.preventDefault();
    event?.stopPropagation();
    if (event?.type === 'dragenter' || event?.type === 'dragover') {
      setDragActive(true);
    } else if (event?.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((event) => {
    event?.preventDefault();
    event?.stopPropagation();
    setDragActive(false);

    if (event?.dataTransfer?.files && event.dataTransfer.files[0]) {
      handleFile(event.dataTransfer.files[0]);
    }
  }, []);

  const handleChange = (event) => {
    event?.preventDefault();
    if (event?.target?.files && event.target.files[0]) {
      handleFile(event.target.files[0]);
    }
  };

  const handleFile = (file) => {
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/plain'
    ];

    if (!allowedTypes.includes(file.type)) {
      setError('Please upload a PDF, DOC, DOCX, or TXT file.');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError('File size should be less than 5MB.');
      return;
    }

    setUploadedFile(file);
    setAnalysisResults(null);
    setUsageLimitMessage(null);
    setError(null);
  };

  const handleAnalyze = async () => {
    // Frontend rate limiting - prevent rapid successive requests
    const MIN_INTERVAL = 5000; // 5 seconds between requests
    if (lastAnalysisTime) {
      const timeSince = Date.now() - lastAnalysisTime;
      if (timeSince < MIN_INTERVAL) {
        const waitTime = Math.ceil((MIN_INTERVAL - timeSince) / 1000);
        setError(`Please wait ${waitTime} second${waitTime > 1 ? 's' : ''} before analyzing again`);
        return;
      }
    }

    if (!uploadedFile || !user || !hasCredits) {
      setError('Cannot analyze: missing file, user, or credits');
      return;
    }

    if (!jobInput) {
      const message =
        'Please provide the company name and paste the full job description before analyzing.';
      setError(message);
      onJobInputError?.(message);
      return;
    }

    setIsAnalyzing(true);
    setError(null);
    setUsageLimitMessage(null);
    setAnalysisProgress(10);
    setAnalysisStage('Preparing resume…');

    // Set timestamp for rate limiting
    setLastAnalysisTime(Date.now());

    try {
      setAnalysisStage('Extracting resume text…');
      setAnalysisProgress(30);
      const resumeText = await ResumeAnalysisService.extractTextFromFile(uploadedFile);

      setAnalysisStage('Analyzing job requirements...');
      setAnalysisProgress(50);

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token;

      const response = await fetch(`${supabaseUrl}/functions/v1/live-analysis`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': supabaseAnonKey,
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {})
        },
        body: JSON.stringify({
          resumeText,
          jobInput,
          userTier,
          previousAnalysisId // Pass to Edge Function for re-scan tracking
        })
      });

      const payload = await response.json().catch(() => null);

      // Handle 504 Gateway Timeout explicitly (Edge Function timeout)
      if (response.status === 504) {
        throw new Error(
          'Analysis took too long and timed out. Your credit was NOT deducted. ' +
          'Please try again with a shorter resume or simpler job description.'
        );
      }

      if (!response.ok) {
        if (payload?._locked) {
          setUsageLimitMessage(
            payload?._message || "You've used your free analysis. Upgrade to continue."
          );
          setAnalysisProgress(0);
          setAnalysisStage('');
          onJobInputError?.('');
          return;
        }

        throw new Error(payload?.error || 'Live job analysis failed');
      }

      setAnalysisProgress(100);
      setAnalysisStage('Analysis complete!');

      setAnalysisResults(payload);

      // Note: Edge Function now stores results and deducts credits
      // Now run comparison logic for re-scans (frontend-only)
      if (user && payload && previousAnalysisId) {
        try {
          const { default: ComparisonService } = await import('../../../services/comparisonService');

          // Generate enhanced LLM-powered comparison
          const enhancedComparison = await ComparisonService.generateEnhancedComparison(
            user.id,
            payload.analysisId, // From Edge Function storage
            previousAnalysisId
          );

          if (enhancedComparison) {
            // Attach comparison to payload for results page
            payload.enhanced_comparison = enhancedComparison;

            // CRITICAL: Save to database so it persists for dashboard view
            const { error: updateError } = await supabase
              .from('resume_analyses')
              .update({ enhanced_comparison: enhancedComparison })
              .eq('id', payload.analysisId);

            if (updateError) {
              console.error('⚠️ Failed to save comparison to DB:', updateError);
            } else {
              console.log('✅ Comparison saved to database');
            }
          }
        } catch (compError) {
          console.error('⚠️ Comparison generation failed (non-blocking):', compError);
        }
      }

      // Refresh user profile to update credit count in UI
      if (user && refreshUserProfile) {
        refreshUserProfile().catch(err => console.log('Profile refresh failed:', err));
      }

      onAnalysisComplete?.(payload);
    } catch (analysisError) {
      console.error('Live analysis failed:', analysisError);

      // Provide clear, actionable error message
      let userMessage = 'Analysis failed. Please try again.';

      // Network errors
      if (analysisError.message?.includes('fetch') || analysisError.message?.includes('network')) {
        userMessage = 'Network error. Please check your connection and try again.';
      }
      // AI/API errors
      else if (analysisError.message?.includes('AI') || analysisError.message?.includes('invalid response')) {
        userMessage = 'AI service error. Please try scanning again in a moment.';
      }
      // Timeout errors
      else if (analysisError.message?.includes('timeout') || analysisError.message?.includes('abort')) {
        userMessage = 'Request timed out. Please try again.';
      }
      // Use API error message if available
      else if (analysisError.message) {
        userMessage = analysisError.message;
      }

      setError(userMessage);
      onJobInputError?.(userMessage);
      setAnalysisProgress(0);
      setAnalysisStage('');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const removeFile = () => {
    setUploadedFile(null);
    setAnalysisProgress(0);
    setAnalysisStage('');
    setAnalysisResults(null);
    setUsageLimitMessage(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const getFileIcon = () => {
    if (!uploadedFile) return <FileText className="w-8 h-8" />;
    if (uploadedFile.type.includes('pdf')) return <FileText className="w-8 h-8 text-red-500" />;
    if (uploadedFile.type.includes('word')) return <FileText className="w-8 h-8 text-blue-500" />;
    return <FileText className="w-8 h-8 text-gray-500" />;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  if (analysisResults) {
    return (
      <div className="bg-card border border-success/40 rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-4">
          <CheckCircle className="w-6 h-6 text-success" />
          <h3 className="text-lg font-semibold text-foreground">Analysis Complete!</h3>
        </div>
        <div className="bg-success/10 rounded-lg p-4 mb-4 text-center">
          <div className="text-3xl font-bold text-success mb-2">
            {analysisResults?.analysis?.job_match_score ??
              analysisResults?.analysis?.overall_resume_score ??
              0}
            %
          </div>
          <div className="text-sm text-muted-foreground">Overall Fit Score</div>
        </div>
        <p className="text-sm text-muted-foreground">Redirecting to your live results dashboard…</p>
      </div>
    );
  }

  return (
    <div className="w-full max-w-2xl mx-auto space-y-6">
      <div
        className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-colors ${dragActive ? 'border-primary bg-primary/5' : 'border-border bg-muted/30 hover:bg-muted/40'
          }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.doc,.docx,.txt,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
          onChange={handleChange}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          disabled={isAnalyzing}
        />

        {uploadedFile ? (
          <div className="space-y-4">
            <div className="flex items-center justify-center space-x-3">
              {getFileIcon()}
              <div className="text-left">
                <p className="text-sm font-medium text-foreground">{uploadedFile.name}</p>
                <p className="text-xs text-muted-foreground">{formatFileSize(uploadedFile.size)}</p>
              </div>
              <Button
                onClick={removeFile}
                variant="ghost"
                size="sm"
                className="text-destructive hover:text-destructive/80"
                disabled={isAnalyzing}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <Upload className="w-12 h-12 text-muted-foreground mx-auto" />
            <div>
              <p className="text-lg font-medium text-foreground">Drop your resume here</p>
              <p className="text-sm text-muted-foreground">or click to browse files</p>
            </div>
            <p className="text-xs text-muted-foreground">Supports PDF, DOC, DOCX, TXT (max 5MB)</p>
          </div>
        )}
      </div>

      {isAnalyzing && (
        <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Loader2 className="w-5 h-5 animate-spin text-primary" />
              <span className="text-sm font-medium text-primary">{analysisStage}</span>
            </div>
            <span className="text-sm text-primary">{analysisProgress}%</span>
          </div>
          <div className="w-full bg-primary/20 rounded-full h-2">
            <div
              className="bg-primary h-2 rounded-full transition-all duration-300"
              style={{ width: `${analysisProgress}%` }}
            />
          </div>
          <p className="text-xs text-primary/80">
            Your resume and job data are being analyzed securely.
          </p>
          <p className="text-xs text-amber-600 font-medium mt-2">
            ⏱️ This may take a few seconds. Please don't close this tab.
          </p>
        </div>
      )}

      {usageLimitMessage && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex flex-col items-center space-y-3 text-center">
          <AlertCircle className="w-5 h-5 text-amber-500" />
          <div>
            <p className="text-sm font-medium text-amber-900">Upgrade required</p>
            <p className="text-xs text-amber-700 mt-1">{usageLimitMessage}</p>
          </div>
          <Button
            size="sm"
            onClick={() => navigate('/upgrade')}
            className="px-4"
          >
            Upgrade / Top Up Credits
          </Button>
        </div>
      )}

      {error && (
        <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-4">
          <div className="flex items-start space-x-3 mb-3">
            <AlertCircle className="w-5 h-5 text-destructive mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-destructive">Analysis Failed - No Credit Used</p>
              <p className="text-xs text-destructive/80 mt-1">{error}</p>
              <p className="text-xs text-muted-foreground mt-2">
                Your credit was not deducted. You can try again without losing credits.
              </p>
            </div>
          </div>
          <Button
            onClick={() => {
              setError(null);
              setUploadedFile(null);
              if (fileInputRef.current) {
                fileInputRef.current.value = '';
              }
            }}
            variant="outline"
            size="sm"
            className="w-full sm:w-auto"
          >
            🔄 Try Again
          </Button>
        </div>
      )}

      {uploadedFile && !isAnalyzing && !analysisResults && (
        <div className="flex flex-col sm:flex-row gap-3">
          <Button onClick={handleAnalyze} className="flex-1" disabled={!hasCredits || isAnalyzing}>
            {hasCredits ? 'Analyze Resume (1 Credit)' : 'No Credits Available'}
          </Button>
          <Button onClick={removeFile} variant="outline" className="flex-1 sm:flex-none">
            Remove File
          </Button>
        </div>
      )}

      {!hasCredits && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start space-x-3">
          <AlertCircle className="w-5 h-5 text-amber-500 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-900">No credits available</p>
            <p className="text-xs text-amber-700 mt-1">
              You've used your free analysis credit. Upgrade to premium to unlock live
              analyses and personalized roadmaps.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default FileUploadZone;